// BL-41 — one therapist's or rider's bookings on the shared table (W1): search,
// a status filter with counts, a date range, sorting, paging and export.
//
// The amount column, and the amount in the export, appear only when the viewer
// may see money (admin/superadmin). A hub manager's history is the work, not
// the takings — the same line the totals strip draws.

import { bookingGroupStatus, byRowId, groupRowsByBooking, type BookingRows } from "@bednbody/logic";
import {
  AdminDataTable,
  AdminTableIdentity,
  type AdminTableExportColumn,
  type AdminTableFacet,
} from "@bednbody/ui/components/admin-data-table";
import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { formatPHP } from "@bednbody/ui/lib/format";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { BookingDetailModal } from "@/features/ops-dashboard/components/BookingDetailModal";
import { BOOKING_STATUS_LABEL, bookingStatusLabel } from "@/lib/bookingStatusLabels";
import type { RosterPerson } from "../components/types";
import { useStaffBookings } from "./staff-detail.hooks";

type Booking = NonNullable<ReturnType<typeof useStaffBookings>["data"]>[number];
type Group = BookingRows<Booking>;

const groupStatus = (group: Group) => bookingGroupStatus(group.map((row) => row.status));
const groupAmount = (group: Group) => group.reduce((sum, row) => sum + row.amount_due, 0);

function serviceLabel(booking: Booking): string {
  const name = booking.service_variants?.services?.name ?? "—";
  const duration = booking.service_variants?.duration_minutes;
  return duration ? `${name} · ${duration} min` : name;
}

/** A colour per outcome, so a cancelled booking is visible at a glance. */
const STATUS_TONE: Partial<Record<string, BadgeVariant>> = {
  completed: "success-soft",
  cancelled: "destructive-soft",
  pending: "warning-soft",
  awaiting_therapist: "warning-soft",
  awaiting_rider: "warning-soft",
};

const scheduledLabel = (value: string) =>
  new Date(value).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });

/** yyyy-mm-dd in Manila's local day, to compare with the date inputs. */
const localDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Only the statuses this person actually has. Listing all nine put a row of
 * zero-count chips in front of the three that mattered, and cut the row off.
 */
function statusFacets(groups: Group[]): AdminTableFacet<Group>[] {
  const present = new Set(groups.map(groupStatus));
  return [
    {
      id: "status",
      label: "Status",
      options: Object.entries(BOOKING_STATUS_LABEL)
        .filter(([value]) => present.has(value as ReturnType<typeof groupStatus>))
        .map(([value, label]) => ({ value, label })),
      getValue: groupStatus,
    },
  ];
}

export function StaffBookingHistory({
  person,
  canSeeMoney,
}: {
  person: RosterPerson;
  canSeeMoney: boolean;
}) {
  const { data: bookings, isLoading, isError } = useStaffBookings(person);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);

  // Grouped before filtering, so every client of one booking stays one row.
  const allGroups = useMemo(
    () => groupRowsByBooking(bookings ?? [], (row) => row.booking_group_id, byRowId),
    [bookings],
  );
  const groups = useMemo(
    () =>
      allGroups.filter((group) => {
        const day = localDay(group[0].scheduled_at);
        return (!from || day >= from) && (!to || day <= to);
      }),
    [allGroups, from, to],
  );
  const facets = useMemo(() => statusFacets(allGroups), [allGroups]);

  const columns: ColumnDef<Group, unknown>[] = [
    {
      id: "ref",
      accessorFn: (group) => group[0].ref,
      header: "Booking",
      cell: ({ row }) => (
        <AdminTableIdentity
          fallback="BK"
          subtitle={
            row.original.length > 1
              ? `${row.original.length} clients · ${serviceLabel(row.original[0])}`
              : serviceLabel(row.original[0])
          }
          title={row.original[0].ref}
        />
      ),
    },
    {
      id: "scheduled",
      accessorFn: (group) => group[0].scheduled_at,
      header: "Scheduled",
      cell: ({ row }) => scheduledLabel(row.original[0].scheduled_at),
    },
    {
      id: "hub",
      accessorFn: (group) => group[0].hubs?.name ?? "",
      header: "Hub",
      cell: ({ row }) => row.original[0].hubs?.name ?? "—",
    },
    ...(canSeeMoney
      ? [
          {
            id: "amount",
            accessorFn: groupAmount,
            header: "Amount due",
            cell: ({ row }: { row: { original: Group } }) => formatPHP(groupAmount(row.original)),
          } satisfies ColumnDef<Group, unknown>,
        ]
      : []),
    {
      id: "status",
      accessorFn: groupStatus,
      header: "Status",
      cell: ({ row }) => (
        <Badge shape="soft" variant={STATUS_TONE[groupStatus(row.original)] ?? "info-soft"}>
          {bookingStatusLabel(groupStatus(row.original))}
        </Badge>
      ),
    },
  ];

  const exportColumns: AdminTableExportColumn<Group>[] = [
    { header: "Booking", value: (group) => group[0].ref },
    { header: "Scheduled", value: (group) => group[0].scheduled_at },
    { header: "Service", value: (group) => group.map(serviceLabel).join(", ") },
    { header: "Hub", value: (group) => group[0].hubs?.name ?? "" },
    ...(canSeeMoney ? [{ header: "Amount due", value: groupAmount }] : []),
    { header: "Status", value: groupStatus },
  ];

  const fromId = `history-from-${person.id}`;
  const toId = `history-to-${person.id}`;

  return (
    <>
      <AdminDataTable
        presentation="embedded"
        columns={columns}
        data={groups}
        emptyDescription={
          isError
            ? "The bookings could not be loaded. Try again in a moment."
            : from || to
              ? "Nothing in this date range."
              : "No bookings yet."
        }
        emptyTitle={isError ? "Couldn't load bookings" : "No bookings"}
        exportColumns={exportColumns}
        exportFilename={`bookings-${person.name.toLowerCase().replace(/\s+/g, "-")}`}
        facets={facets}
        getRowId={(group) => group[0].booking_group_id ?? group[0].id}
        description={
          bookings
            ? `${allGroups.length} ${allGroups.length === 1 ? "booking" : "bookings"} · newest first. Click a row to open it.`
            : "Loading…"
        }
        header={
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-sm font-normal text-muted-foreground" htmlFor={fromId}>
              From
            </Label>
            <Input
              className="h-9 w-40 rounded-lg border-border bg-background"
              id={fromId}
              max={to || undefined}
              onChange={(event) => setFrom(event.target.value)}
              type="date"
              value={from}
            />
            <Label className="text-sm font-normal text-muted-foreground" htmlFor={toId}>
              to
            </Label>
            <Input
              className="h-9 w-40 rounded-lg border-border bg-background"
              id={toId}
              min={from || undefined}
              onChange={(event) => setTo(event.target.value)}
              type="date"
              value={to}
            />
            {from || to ? (
              <Button
                onClick={() => {
                  setFrom("");
                  setTo("");
                }}
                shape="soft"
                size="sm"
                variant="ghost"
              >
                Clear
              </Button>
            ) : null}
          </div>
        }
        initialSorting={[{ id: "scheduled", desc: true }]}
        onRowClick={(group) => setDetailId(group[0].id)}
        rowLabel={(group) => `Open ${group[0].ref}`}
        title="Booking history"
        loading={isLoading}
        searchPlaceholder="Search booking ref or service…"
        searchText={(group) =>
          [group[0].ref, ...group.map(serviceLabel), group[0].hubs?.name].join(" ")
        }
      />
      <BookingDetailModal
        bookingId={detailId}
        isOpen={detailId !== null}
        onClose={() => setDetailId(null)}
      />
    </>
  );
}
